/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';

import {
  AIDE_MATERIELLE_FIXE,
  AIDE_MATERIELLE_PROPORTIONNELLE,
  REGIME_BIEN_INDIVISION,
  REGIME_BIEN_LEGAL,
} from '@/constants';
import { Sexe, TypeDateNaissance } from '@/enums';
import formatPhoneNumber from './formatPhoneNumber';
import sexeToSexeCode from './sexeToSexeCode';

/**
 * Transpose un télédossier Hubee de démarche PACS vers le modèle de saisie de
 * l'assistant (même forme que `DEFAULT_PACS`).
 *
 * Le résultat est volontairement *partiel* : il ne contient que les champs
 * réellement portés par le télédossier, pour être fusionné par-dessus le PACS
 * initialisé par `AssistantContextProvider` (registre, officier, valeurs par
 * défaut du paramétrage) sans écraser ce dernier avec des `null`.
 *
 * Forme d'entrée (cf. colonnes de la démarche PACS dans
 * `@mairistem/administres-utils`, complétée à la main pour les champs qui
 * n'apparaissent pas dans les colonnes de liste) :
 *   teledossier.partenaires.partenaire1 / .partenaire2
 *   teledossier.partenaires.partenaire{1,2}.parent1 / .parent2
 *   teledossier.partenaires.couple.residenceCommune
 */

const nullifyEmpty = (value: unknown): string | null =>
  value === null || value === undefined || value === '' ? null : String(value);

// Le sexe Hubee ('homme'/'femme') utilise déjà les valeurs de l'enum Sexe ;
// on se contente de valider, les autres flux (M/F) étant tolérés.
const resolveSexe = (sexe: string | null | undefined): string | null => {
  if (sexe === Sexe.Homme || sexe === Sexe.Femme || sexe === Sexe.Indetermine) {
    return sexe;
  }

  const code = _.toUpper(_.trim(sexe ?? '')).replace(/\.$/, '');
  if (code === 'M') return Sexe.Homme;
  if (code === 'F') return Sexe.Femme;
  if (code === 'I') return Sexe.Indetermine;

  return null;
};

// Le formulaire attend un numéro au format international formaté.
const toInternational = (phone: string | null | undefined): string | null => {
  const value = nullifyEmpty(phone);
  if (!value) return null;

  const digits = value.replace(/\D/g, '');
  if (digits.length !== 10 || !digits.startsWith('0')) return value;

  return formatPhoneNumber(`+33${digits.substring(1)}`) || null;
};

// Hubee peut concaténer les prénoms dans `prenom` : le premier alimente
// `prenom`, le reste `prenomsComplementaires` (même règle que
// `mapConventionToPacs`).
const splitPrenom = (
  prenom: string | null | undefined,
  prenomsComplementaires: string | null | undefined,
) => {
  const value = nullifyEmpty(prenom);
  const complementaires = nullifyEmpty(prenomsComplementaires);

  if (!value) return { prenom: null, prenomsComplementaires: complementaires };

  const commaIndex = value.indexOf(',');
  if (commaIndex === -1) {
    return { prenom: value.trim(), prenomsComplementaires: complementaires };
  }

  return {
    prenom: value.substring(0, commaIndex).trim(),
    prenomsComplementaires:
      value.substring(commaIndex + 1).trim() || complementaires,
  };
};

/** Adresse Hubee -> `adresseNaissance` d'un partenaire */
const mapAdresseNaissance = (adresse: any) => {
  if (!adresse) return undefined;

  return _.omitBy(
    {
      libelleCodePostal: nullifyEmpty(adresse.libelleCodePostal),
      libelleCommune: nullifyEmpty(adresse.libelleCommune),
      libellePays: nullifyEmpty(adresse.libellePays),
      libelleDepartement: nullifyEmpty(adresse.libelleDepartement),
      codeDepartement: nullifyEmpty(adresse.codeDepartement),
    },
    _.isNil,
  );
};

/** Adresse Hubee -> `adresseResidence` d'un partenaire (modèle interne) */
const mapAdresseResidence = (adresse: any) => {
  if (!adresse) return undefined;

  return _.omitBy(
    {
      // Commune / Hors Commune / Etranger, déduit par l'API ; absent, le
      // formulaire retombe sur « Commune ».
      type: nullifyEmpty(adresse.type),
      libelleCodePostal: nullifyEmpty(adresse.libelleCodePostal),
      libelleCommune: nullifyEmpty(adresse.libelleCommune),
      libellePays: nullifyEmpty(adresse.libellePays),
      libelleDepartement: nullifyEmpty(adresse.libelleDepartement),
      codeDepartement: nullifyEmpty(adresse.codeDepartement),
      libelleVoie: nullifyEmpty(adresse.libelleVoie),
      indiceRepetition: nullifyEmpty(adresse.indiceRepetition),
      complementDistribution: nullifyEmpty(adresse.complementDistribution),
      numeroVoirie: nullifyEmpty(adresse.numeroVoirie),
      complementNumeroVoirie: nullifyEmpty(adresse.complementNumeroVoirie),
      complementIdentification: nullifyEmpty(adresse.complementIdentification),
      complementGeographique: nullifyEmpty(adresse.complementGeographique),
      typeVoie: nullifyEmpty(adresse.typeVoie),
    },
    _.isNil,
  );
};

/** Adresse Hubee -> adresse d'acte (`adresseCommune`, `adresseDeclaration`…) */
const mapAdresseActe = (adresse: any) => {
  if (!adresse) return undefined;

  return _.omitBy(
    {
      type: nullifyEmpty(adresse.type),
      libellePays: nullifyEmpty(adresse.libellePays),
      libelleDepartement: nullifyEmpty(adresse.libelleDepartement),
      codeDepartement: nullifyEmpty(adresse.codeDepartement),
      codePostal: nullifyEmpty(adresse.libelleCodePostal),
      libelleCommune: nullifyEmpty(adresse.libelleCommune),
      localite: nullifyEmpty(adresse.libelleCommune),
      numeroVoie: nullifyEmpty(adresse.numeroVoirie),
      complementNumeroVoie: nullifyEmpty(adresse.complementNumeroVoirie),
      complementIdentification: nullifyEmpty(adresse.complementIdentification),
      complementGeographique: nullifyEmpty(adresse.complementGeographique),
      complementDistribution: nullifyEmpty(adresse.complementDistribution),
      libelleVoie: nullifyEmpty(adresse.libelleVoie),
    },
    _.isNil,
  );
};

/**
 * `nationalites` (jusqu'à 3 entrées `{ code, identifiant, libelle, nationalite }`)
 * -> `premiereNationalite` / `deuxiemeNationalite` / `troisiemeNationalite`.
 *
 * C'est le nombre de ces trois champs renseignés qui pilote le nombre de
 * sélecteurs affichés par `EtatCivil` (cf. `useNationalitesField` dans
 * `@mairistem/administres-utils`) : les remplir suffit à faire apparaître les
 * 2ᵉ et 3ᵉ nationalités, sans passer par le bouton « + ».
 */
const mapNationalites = (nationalites: any) => {
  const libelles = _.map(
    _.take(_.filter(nationalites, (n) => !_.isNil(n)), 3),
    (nationalite: any) => nullifyEmpty(nationalite?.nationalite),
  );

  return _.omitBy(
    {
      premiereNationalite: libelles[0] ?? null,
      deuxiemeNationalite: libelles[1] ?? null,
      troisiemeNationalite: libelles[2] ?? null,
    },
    _.isNil,
  );
};

// Les champs oui/non du formulaire Hubee arrivent tantôt en booléen, tantôt en
// libellé ('Oui'/'Non'). Tout le reste (absent, vide) laisse le champ vide pour
// que l'agent tranche.
const toBoolean = (value: unknown): boolean | null => {
  if (value === true || value === false) return value;

  const libelle = _.toLower(_.trim(String(value ?? '')));
  if (libelle === 'oui' || libelle === 'true' || libelle === '1') return true;
  if (libelle === 'non' || libelle === 'false' || libelle === '0') return false;

  return null;
};

// `situationFamiliale` -> code INSEE d'état matrimonial antérieur (C/D/V).
const resolveEtatMatrimonial = (situationFamiliale: unknown): string | null => {
  const situation = _.deburr(_.toLower(_.trim(String(situationFamiliale ?? ''))));
  if (!situation) return null;

  if (_.startsWith(situation, 'celibataire')) return 'C';
  if (_.startsWith(situation, 'divorce')) return 'D';
  if (_.startsWith(situation, 'veu')) return 'V';

  return null;
};

/**
 * Parent d'un partenaire (`partenaire{N}.parent1` / `.parent2`, chacun côté
 * mère et père indifféremment — Hubee ne les distingue pas par genre, l'UI de
 * l'assistant non plus : « Parent 1 » / « Parent 2 » restent des libellés
 * neutres). Seuls nom, prénom et sexe sont fiables côté Hubee ; le reste
 * (naissance, adresse...) est laissé à la saisie de l'agent de mairie.
 */
const mapParent = (parent: any) => {
  if (!parent) return undefined;

  const sexe = resolveSexe(parent.sexe);
  const { prenom, prenomsComplementaires } = splitPrenom(
    parent.prenom,
    parent.prenomsComplementaires,
  );

  return _.omitBy(
    {
      sexe,
      sexeCode: sexeToSexeCode(sexe),
      nomFamille: nullifyEmpty(parent.nomFamille),
      nomUsage: nullifyEmpty(parent.nomUsage),
      prenom,
      prenomsComplementaires,
    },
    _.isNil,
  );
};

/** Composante de date du flux (texte, ex. « 08 ») -> nombre, null si absente */
const toEntier = (valeur: unknown): number | null => {
  const nombre = Number.parseInt(String(valeur ?? ''), 10);
  return Number.isNaN(nombre) ? null : nombre;
};

/**
 * Date de naissance d'un partenaire, avec le mode de saisie correspondant à
 * ce que la demande renseigne : complète, mois + année (jour inconnu) ou
 * année seule. Sans mode, le champ reste en « date complète » et n'affiche
 * rien dès qu'une composante manque.
 */
const mapDateNaissance = (partenaire: any) => {
  const jour = toEntier(partenaire.jourNaissance);
  const mois = toEntier(partenaire.moisNaissance);
  const annee = toEntier(partenaire.anneeNaissance);

  let typeDateNaissance: TypeDateNaissance | null = null;
  if (annee !== null && mois !== null && jour !== null) {
    typeDateNaissance = TypeDateNaissance.COMPLET;
  } else if (annee !== null && mois !== null) {
    typeDateNaissance = TypeDateNaissance.MOIS_ANNEE;
  } else if (annee !== null) {
    typeDateNaissance = TypeDateNaissance.ANNEE;
  }

  return {
    jourNaissance: typeDateNaissance === TypeDateNaissance.COMPLET ? jour : null,
    moisNaissance: typeDateNaissance === TypeDateNaissance.ANNEE ? null : mois,
    anneeNaissance: annee,
    typeDateNaissance,
  };
};

const mapPartenaire = (partenaire: any) => {
  if (!partenaire) return undefined;

  const sexe = resolveSexe(partenaire.sexe);
  const { prenom, prenomsComplementaires } = splitPrenom(
    partenaire.prenom,
    partenaire.prenomsComplementaires,
  );

  return _.omitBy(
    {
      sexe,
      sexeCode: sexeToSexeCode(sexe),
      nomFamille: nullifyEmpty(partenaire.nomFamille),
      nomUsage: nullifyEmpty(partenaire.nomUsage),
      prenom,
      prenomsComplementaires,
      ...mapDateNaissance(partenaire),
      ...mapNationalites(partenaire.nationalites),
      tel1: toInternational(partenaire.tel1),
      tel2: toInternational(partenaire.tel2),
      mail: nullifyEmpty(partenaire.mail),
      adresseNaissance: mapAdresseNaissance(partenaire.adresseNaissance),
      // L'adresse de contact prime sur l'adresse de rattachement quand les deux
      // sont renseignées : c'est celle que le citoyen a saisie pour la demande.
      adresseResidence: mapAdresseResidence(
        partenaire.adresseResidence ??
          partenaire.adresseContact ??
          partenaire.adresseRattachement,
      ),
      // Informations complémentaires
      protectionJuridique: toBoolean(partenaire.mesureJuridique),
      // Balise vide quand la question ne s'applique pas : non protégé(e).
      protectionOfpra: toBoolean(partenaire.ofpra) ?? false,
    },
    _.isNil,
  );
};

/**
 * Aide matérielle d'une convention-type : fixée au montant s'il est présent,
 * proportionnelle sinon.
 *
 * `typeAideMateriel` est ignoré : HubEE envoie toujours « aideProportionnel »,
 * même quand le demandeur choisit « Fixée à un montant ». C'est la présence du
 * montant qui fait foi (il disparaît si le demandeur repasse en
 * proportionnelle). Constaté sur les demandes de recette du 14/10/2026.
 */
/** Codes de typeConvention (cf. OPTIONS_TYPE_CONVENTION_FICHE) */
const TYPE_CONVENTION_TYPE = 1;
const TYPE_CONVENTION_SPECIFIQUE = 2;

/**
 * Convention choisie dans la demande. Une convention personnalisée
 * (`specifique`) est rédigée par les partenaires et jointe à la demande :
 * l'aide matérielle et le régime des biens, propres à la convention-type,
 * restent vides.
 */
const mapConvention = (convention: any) => {
  if (!convention) return {};

  if (convention.specifique === true) {
    return { typeConvention: TYPE_CONVENTION_SPECIFIQUE };
  }

  return {
    typeConvention: TYPE_CONVENTION_TYPE,
    ...mapAideMaterielle(convention),
    regimeBien: resolveRegimeBien(convention.regimePacs),
  };
};

const mapAideMaterielle = (convention: any) => {
  if (!convention) return {};

  const montant = convention.montantAideMateriel;

  if (_.isNil(montant) || montant === '') {
    return { aideMaterielle: AIDE_MATERIELLE_PROPORTIONNELLE, valeurAide: null };
  }

  return {
    aideMaterielle: AIDE_MATERIELLE_FIXE,
    valeurAide: _.toNumber(montant),
  };
};

/**
 * Régime des biens : `regimePacs` vaut « legal » (séparation des patrimoines)
 * ou « indivision ». Toute autre valeur laisse le choix à l'agent.
 */
const resolveRegimeBien = (regimePacs: unknown): number | null => {
  const regime = _.deburr(_.toLower(_.trim(String(regimePacs ?? ''))));

  if (regime === 'legal') return REGIME_BIEN_LEGAL;
  if (regime === 'indivision') return REGIME_BIEN_INDIVISION;

  return null;
};

const mapTeledossierToPacs = (teledossier: any): any => {
  const { partenaires } = teledossier || {};
  if (!partenaires) return {};

  const { couple } = partenaires;

  const conventionDeclaration = _.omitBy(
    {
      partenaire1: mapPartenaire(partenaires.partenaire1),
      partenaire2: mapPartenaire(partenaires.partenaire2),
      // parent1/parent2 sont deux emplacements neutres (pas mère/père), repris
      // à la même position dans le formulaire : « Parent 1 » est le champ
      // perePartenaireX, « Parent 2 » le champ merePartenaireX.
      perePartenaire1: mapParent(partenaires.partenaire1?.parent1),
      merePartenaire1: mapParent(partenaires.partenaire1?.parent2),
      perePartenaire2: mapParent(partenaires.partenaire2?.parent1),
      merePartenaire2: mapParent(partenaires.partenaire2?.parent2),
      adresseCommune: mapAdresseActe(couple?.residenceCommune),
      // Attestations sur l'honneur de l'étape 8
      attestationLienParente: toBoolean(couple?.nonParente),
      attestationAdresseCommune: toBoolean(couple?.residenceCommuneAttestee),
      ...mapConvention(couple?.convention),
    },
    _.isNil,
  );

  // `complementInsee` vit à la racine du PACS, pas dans la convention.
  const complementInsee = _.omitBy(
    {
      p1EtatMatrimonialCode: resolveEtatMatrimonial(
        partenaires.partenaire1?.situationFamiliale,
      ),
      p2EtatMatrimonialCode: resolveEtatMatrimonial(
        partenaires.partenaire2?.situationFamiliale,
      ),
    },
    _.isNil,
  );

  return _.omitBy(
    {
      conventionDeclaration: _.isEmpty(conventionDeclaration)
        ? undefined
        : conventionDeclaration,
      complementInsee: _.isEmpty(complementInsee) ? undefined : complementInsee,
    },
    _.isNil,
  );
};

/** Clés d'adresse produites par le mapping (cf. mapAdresse*). */
const CLES_ADRESSE = ['adresseNaissance', 'adresseResidence', 'adresseCommune'];

/**
 * Applique le préremplissage Hubee sur le PACS initialisé.
 *
 * Fusion profonde, sauf pour les adresses : celle de la demande remplace
 * entièrement celle issue du registre. Fusionnées champ à champ, une adresse
 * Hubee partielle (ex. commune sans département) hériterait du département
 * ou de la commune déléguée du registre et deviendrait incohérente.
 *
 * Mute et retourne `pacs`, comme `_.merge`.
 */
export const fusionnerPrefillHubee = (pacs: any, prefill: any): any =>
  _.mergeWith(pacs, prefill, (_valeur, source, cle) =>
    CLES_ADRESSE.includes(cle as string) && _.isPlainObject(source)
      ? _.cloneDeep(source)
      : undefined,
  );

export default mapTeledossierToPacs;
